import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch
} from 'antd';
import { FormItemWrap } from '@unicus/react-ui-kit';
import {
  FormItemUserDetail,
  FormListItemWrap,
  FormListWrap,
  InputCurrency,
  RemoteSelectTree,
  ResCol,
  SelectBoolean,
  SelectPaymentMethod,
  UploadList
} from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { closeViewDetail } from 'stores/financialEntries/financialEntryActions';
import moment from 'moment';
import { normalizeToSelect } from 'util/converters';
import RemoteSelect from 'components/RemoteSelect';
import { PAYMENT, RECEIPT } from 'constants/types';
import { EyeOutlined } from '@ant-design/icons';
import useSegmentField from 'core/hooks/useSegmentField';
import FinancialEntryInstallmentNumber from './FinancialEntryInstallmentNumber';
import FinancialEntryFormPaymentItem from './FinancialEntryFormPaymentItem';

const FinancialEntryViewDetailInternal = ({ data }) => {
  const dispatch = useDispatch();
  const segmentFields = useSegmentField();
  const [form] = Form.useForm();
  const [fieldEditing, setFieldEditing] = useState(false);
  const { type } = data;

  const handleCancel = () => {
    dispatch(closeViewDetail());
  };

  const handleBeforePaymentBinding = (payment) => {
    if (!payment) {
      return null;
    }

    return { ...payment, date: moment(payment.date) };
  };

  const handleBeforeExportAuditBinding = (exportAudit) => {
    if (!exportAudit) {
      return null;
    }

    return { ...exportAudit, date: moment(exportAudit.date) };
  };

  const handleBeforeBinding = ({
    checkingAccount,
    type,
    amount = 0,
    effectiveDate,
    patient,
    customer,
    supplier,
    costCenter,
    attachments = [],
    payment,
    creationTime,
    lastModificationTime,
    items = [],
    exportAudit,
    ...rest
  }) => {
    return {
      ...rest,
      type,
      creationTime: moment(creationTime),
      lastModificationTime: moment(lastModificationTime),
      patient: patient ? normalizeToSelect(patient) : null,
      customer: customer ? normalizeToSelect(customer) : null,
      supplier: supplier ? normalizeToSelect(supplier) : null,
      amount: type === PAYMENT && amount < 0 ? amount * -1 : amount,
      effectiveDate: effectiveDate ? moment(effectiveDate) : moment(),
      checkingAccount: normalizeToSelect(checkingAccount),
      costCenter: normalizeToSelect(costCenter),
      attachments: attachments.map((c) => c.attachment),
      payment: handleBeforePaymentBinding(payment),
      exportAudit: handleBeforeExportAuditBinding(exportAudit),
      items: items.map((c) => ({
        ...c,
        dueDate: moment(c.dueDate),
        checkingAccount: normalizeToSelect(c.checkingAccount),
        exportAudit: handleBeforeExportAuditBinding(c.exportAudit),
        payment: {
          ...c.payment,
          date: c.payment?.date ? moment(c.payment?.date) : null,
          creationTime: c.payment?.creationTime
            ? moment(c.payment?.creationTime)
            : null,
          dateOfCompensation: c.payment?.dateOfCompensation
            ? moment(c.payment?.dateOfCompensation)
            : null
        }
      }))
    };
  };

  const handleChangeField = (field) => {
    setFieldEditing(field);
  };

  const handleFieldClose = () => {
    setFieldEditing(null);
  };

  return (
    <Drawer
      className="gx-drawer-form md"
      visible
      title="Detalhes"
      onClose={handleCancel}
    >
      <Form
        layout="vertical"
        form={form}
        preserve
        initialValues={handleBeforeBinding(data)}
      >
        {fieldEditing && (
          <FinancialEntryFormPaymentItem
            form={form}
            type={type}
            field={fieldEditing}
            onClose={handleFieldClose}
            disabled
            showAudit
          />
        )}
        {data.patient && (
          <Row>
            <ResCol span={24}>
              <FormItemWrap
                name="patient"
                label={segmentFields.serviceRecipient}
              >
                <RemoteSelect resource="patients" disabled />
              </FormItemWrap>
            </ResCol>
          </Row>
        )}
        {data.customer && (
          <Row>
            <ResCol span={24}>
              <FormItemWrap name="customer" label={segmentFields.client}>
                <RemoteSelect resource="customers" disabled />
              </FormItemWrap>
            </ResCol>
          </Row>
        )}
        {data.supplier && (
          <Row>
            <ResCol span={24}>
              <FormItemWrap name="supplier" label="Fornecedor">
                <RemoteSelect resource="suppliers" disabled />
              </FormItemWrap>
            </ResCol>
          </Row>
        )}
        <Row>
          <ResCol span={24}>
            <FormItemWrap name="description" label="Descrição">
              <Input.TextArea maxLength={250} disabled />
            </FormItemWrap>
          </ResCol>
        </Row>

        <Row className="no-padding-last">
          <ResCol span={9}>
            <FormItemWrap name="effectiveDate" label="Competência" required>
              <DatePicker format="DD/MM/YYYY" disabled />
            </FormItemWrap>
          </ResCol>
          <ResCol span={7}>
            <FormItemWrap
              className="ant-form-item-money"
              name="amount"
              label="Valor total"
            >
              <InputCurrency disabled />
            </FormItemWrap>
          </ResCol>

          <ResCol span={7}>
            <FormItemWrap name="totalInstallments" label="N° de parcelas">
              <InputNumber disabled />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Divider orientation="left">Parcelas</Divider>
        <FormListWrap name="items" hideAddButton>
          {({ field, value }) => (
            <>
              <Row>
                <Col span={4}>
                  <FormListItemWrap name="installmentNumber" label="Parcela">
                    <FinancialEntryInstallmentNumber form={form} />
                  </FormListItemWrap>
                </Col>
                <Col span={8}>
                  <FormListItemWrap name="dueDate" label="Vencimento" required>
                    <DatePicker format="DD/MM/YYYY" disabled />
                  </FormListItemWrap>
                </Col>
                <Col span={7}>
                  <FormListItemWrap
                    className="ant-form-item-money"
                    name="amount"
                    label="Valor"
                    required
                  >
                    <InputCurrency disabled />
                  </FormListItemWrap>
                </Col>
                <Col span={4}>
                  <FormListItemWrap
                    name="isPaid"
                    label={type === RECEIPT ? 'Recebido' : 'Pago'}
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormListItemWrap>
                </Col>
                {value.isPaid && (
                  <Col span={1}>
                    <FormListItemWrap name="isPaid" label=" ">
                      <EyeOutlined
                        className="gx-pointer gx-text-info"
                        onClick={() => handleChangeField(field)}
                      />
                    </FormListItemWrap>
                  </Col>
                )}
              </Row>
            </>
          )}
        </FormListWrap>
        <Row>
          <ResCol span={24}>
            <FormItemWrap name="category" label="Categoria">
              <RemoteSelectTree
                resource="categories"
                placeholder="Selecione"
                disabled
                getSubItems={({ subCategories }) => subCategories}
                getTitle={({ name }) => name}
                onDisabled={({ parentCategoryId }) => {
                  return parentCategoryId === null;
                }}
                normalizeItem={({ id, name, type }) => ({
                  id,
                  name,
                  type
                })}
              />
            </FormItemWrap>
          </ResCol>
        </Row>

        <Row>
          <ResCol span={24}>
            <FormItemWrap
              name="costCenter"
              label="Centro de custo"
              hidden={!data.costCenter}
            >
              <RemoteSelect resource="cost-centers" disabled />
            </FormItemWrap>
          </ResCol>
        </Row>

        {data.attachments?.length > 0 ? (
          <>
            <Divider orientation="left">Anexos</Divider>
            <FormItemWrap name="attachments">
              <UploadList disabled />
            </FormItemWrap>
          </>
        ) : null}

        <Divider orientation="left">Auditoria</Divider>
        <Row>
          <ResCol span={12}>
            <FormItemWrap name="creationTime" label="Data de lançamento">
              <DatePicker format="DD/MM/YYYY HH:mm" disabled />
            </FormItemWrap>
          </ResCol>

          <ResCol span={12}>
            <FormItemWrap
              name="lastModificationTime"
              label={
                data.isDeleted ? 'Data da exclusão' : 'Data da última alteração'
              }
              hidden={!data.userLastModifier}
            >
              <DatePicker format="DD/MM/YYYY HH:mm" disabled />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Row>
          <ResCol span={12}>
            <FormItemWrap name="userCreator" label="Usuário de lançamento">
              <FormItemUserDetail />
            </FormItemWrap>
          </ResCol>

          <ResCol span={12}>
            <FormItemWrap
              name="userLastModifier"
              label={
                data.isDeleted
                  ? 'Usuário da exclusão'
                  : 'Usuário da última alteração'
              }
              hidden={!data.userLastModifier}
            >
              <FormItemUserDetail />
            </FormItemWrap>
          </ResCol>
        </Row>

        {data.exportAudit && (
          <>
            <Divider orientation="left">Auditoria contábil</Divider>
            <Row>
              <ResCol span={7}>
                <FormItemWrap
                  name={['exportAudit', 'date']}
                  label="Data da contabilização"
                >
                  <DatePicker format="DD/MM/YYYY HH:mm" disabled />
                </FormItemWrap>
              </ResCol>

              <ResCol span={7}>
                <FormItemWrap name={['exportAudit', 'user']} label="Usuário">
                  <FormItemUserDetail />
                </FormItemWrap>
              </ResCol>
              <ResCol span={10}>
                <FormItemWrap
                  name={['exportAudit', 'isModified']}
                  label="Foi modificado após contabilização?"
                >
                  <SelectBoolean disabled />
                </FormItemWrap>
              </ResCol>
            </Row>
          </>
        )}
      </Form>
    </Drawer>
  );
};

const FinancialEntryViewDetail = () => {
  const { viewDetail } = useSelector(
    (c) => c.resources['financial-entries'] ?? {}
  );

  if (!viewDetail) {
    return null;
  }

  return (
    <FinancialEntryViewDetailInternal
      data={viewDetail}
      resources="financial-entries"
    />
  );
};

export default FinancialEntryViewDetail;


